import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Form, Table } from "react-bootstrap";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { FilterSelect } from "../components/reports/reportShared.jsx";
import { getStrikeRateReport, listEntityRows } from "../services/apiClient.js";
import { useMasterCompanyNames } from "../services/useMasterCompanies.js";
import { monthKey } from "../utils/date.js";

// Strike Rate (legacy StrikeRateReport.aspx): per month of the Requested date,
// accepted vs declined (by reason) + strike rate % = accepted/(accepted+declined).
// 1.0 fixes: dates now filter AND group on the same date (1.0 filtered LogDate
// but grouped RequestedDate); month window is half-open (no extra-instant leak).

// "n months ago" as "YYYY-MM", from the NZ calendar. monthKey() is the sanctioned
// source (src/utils/date.js): the hand-rolled version this replaces read the
// BROWSER's month, which on a Brisbane box is two hours behind Auckland — for the
// first two hours of the 1st, the dropdown offered the wrong month.
const monthAgo = (n) => monthKey(-n);
// FOUR-digit year on purpose: "Aug 25" reads as the 25th of August, not 2025
// (Robert, 2026-08-01). The dropdown already spells the year out, so this also
// makes the table agree with the control that filtered it.
const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-NZ", { month: "short", year: "numeric" });
};

// The document's own subtitle spells it out, like the KPI pack: a heading is read
// once and should not need decoding.
const monthLabelLong = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
};
// The month pickers are DROPDOWNS like 1.0 ("Feb 2026"), newest first —
// a raw month input read as a plain textbox (Robert 2026-07-17).
const MONTH_OPTIONS = Array.from({ length: 36 }, (_, i) => {
  const value = monthAgo(i);
  const [y, mo] = value.split("-");
  const label = new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-NZ", { month: "short", year: "numeric" });
  return { value, label };
});

function MonthSelect({ label, value, onChange }) {
  return (
    <Form.Group>
      <Form.Label className="mb-0 small fw-semibold">{label}</Form.Label>
      <ThemedSelect size="sm" style={{ width: 130 }} value={value} onChange={(e) => onChange(e.target.value)}>
        {MONTH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </ThemedSelect>
    </Form.Group>
  );
}

const BREAKDOWNS = [
  ["assessor", "By Assessor"], ["insurer", "By Insurer"],
  ["company", "By Assessor Company"], ["division", "By Division"],
];

function RateTable({ rows, reasons, groupCol = null }) {
  const totals = {};
  for (const r of rows) {
    for (const k of ["requested", "undetermined", "accepted", "declined"]) totals[k] = (totals[k] || 0) + r[k];
    r.reasons.forEach((x, i) => (totals[`r${i}`] = (totals[`r${i}`] || 0) + x.count));
  }
  const totalRate = totals.accepted + totals.declined > 0
    ? Math.round((totals.accepted / (totals.accepted + totals.declined)) * 100)
    : null;
  return (
    <Table striped bordered hover responsive size="sm" className="align-middle mb-2 report-table">
      <thead className="table-surface">
        <tr>
          {groupCol ? <th>{groupCol}</th> : null}
          <th>Month</th>
          <th className="text-end">Logged</th>
          <th className="text-end">Undetermined</th>
          <th className="text-end">Accepted</th>
          {reasons.map((r) => <th key={r} className="text-end">{r}</th>)}
          <th className="text-end">Total Declined</th>
          <th className="text-end">Strike Rate</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {groupCol ? <td className="text-nowrap">{r.group_name}</td> : null}
            <td className="text-nowrap">{monthLabel(r.month)}</td>
            <td className="text-end">{r.requested}</td>
            <td className="text-end">{r.undetermined}</td>
            <td className="text-end fw-semibold text-success">{r.accepted}</td>
            {r.reasons.map((x) => <td key={x.label} className="text-end">{x.count || ""}</td>)}
            <td className="text-end text-danger">{r.declined}</td>
            <td className="text-end fw-semibold">{r.strike_rate != null ? `${Math.round(r.strike_rate)}%` : "—"}</td>
          </tr>
        ))}
        <tr className="table-surface fw-semibold">
          {groupCol ? <td /> : null}
          <td>Totals</td>
          <td className="text-end">{totals.requested || 0}</td>
          <td className="text-end">{totals.undetermined || 0}</td>
          <td className="text-end text-success">{totals.accepted || 0}</td>
          {reasons.map((r, i) => <td key={r} className="text-end">{totals[`r${i}`] || ""}</td>)}
          <td className="text-end text-danger">{totals.declined || 0}</td>
          <td className="text-end">{totalRate != null ? `${totalRate}%` : "—"}</td>
        </tr>
        {/* 1.0's Percentages strip: each column as a share of Logged, wearing
            the same colours as the Totals row so the eye pairs them. */}
        {!groupCol && totals.requested > 0 ? (
          <tr className="table-surface fst-italic">
            <td className="text-muted">Percentages</td>
            <td className="text-end text-muted">100.00%</td>
            <td className="text-end text-muted">{pctOfLogged(totals.undetermined, totals.requested)}</td>
            <td className="text-end text-success">{pctOfLogged(totals.accepted, totals.requested)}</td>
            {reasons.map((r, i) => (
              <td key={r} className="text-end text-muted">{pctOfLogged(totals[`r${i}`] || 0, totals.requested)}</td>
            ))}
            <td className="text-end text-danger">{pctOfLogged(totals.declined, totals.requested)}</td>
            <td className="text-end fw-semibold">{totalRate != null ? `${totalRate}%` : "—"}</td>
          </tr>
        ) : null}
      </tbody>
    </Table>
  );
}

// Whole percentages. "12.47%" implies a precision that a count of jobs does not
// have, and it is the widest value in every column — rounding buys the width back.
const pctOfLogged = (n, logged) => `${Math.round((100 * (n || 0)) / logged)}%`;

// ALWAYS ON, off the reports master switch (Robert 2026-08-01) — same call as
// Jobs Logged / Accepted and Overruns: GPS needs the accepted-vs-declined numbers
// in front of them for the live-data run, not held behind the phase-1
// verification switch.
function StrikeRateReportPage() {
  // Master companies are data now (Owner Data); same name keeps the diff and
  // the saved-search values identical to the old config constant.
  const COMPANY_GROUP_KEYS = useMasterCompanyNames();
  const [filters, setFilters] = useState({
    from: monthAgo(12), to: monthAgo(0), insurer: "", assessor_id: "",
    assessor_company_id: "", division_id: "", quoted_only: false,
  });
  const [dims, setDims] = useState([]);
  const [lookups, setLookups] = useState({});
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all(
      ["insurer-companies", "assessors", "assessor-companies", "divisions"].map((k) =>
        listEntityRows(k).then((rows) => [k, rows]).catch(() => [k, []])
      )
    ).then((pairs) => setLookups(Object.fromEntries(pairs)));
    load(filters, dims);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load(f = filters, d = dims) {
    setError("");
    try {
      setReport(await getStrikeRateReport({
        ...f, quoted_only: f.quoted_only ? "1" : "", breakdowns: d.join(","),
      }));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the report.");
    }
  }
  const set = (name) => (value) => {
    const next = { ...filters, [name]: value };
    setFilters(next);
    load(next, dims);
  };
  const toggleDim = (key) => {
    const next = dims.includes(key) ? dims.filter((d) => d !== key) : [...dims, key];
    setDims(next);
    load(filters, next);
  };

  const nameOf = (key, id) =>
    (id && (lookups[key] || []).find((r) => String(r.id) === String(id))?.name) || null;
  const scopeLabel =
    (COMPANY_GROUP_KEYS.includes(filters.insurer)
      ? `${filters.insurer} (group)`
      : nameOf("insurer-companies", filters.insurer)) ||
    nameOf("assessor-companies", filters.assessor_company_id) ||
    "All companies";

  return (
    <section className="page-panel">
      {/* App chrome: the document carries its own masthead (report-document.css). */}
      <div className="page-heading no-print"><h2>Strike Rate <RulesHelp topic="strikeRate" /></h2></div>

      <div className="report-controls d-flex align-items-end column-gap-2 row-gap-2 flex-wrap mb-3 no-print">
        <MonthSelect label="From month" value={filters.from} onChange={set("from")} />
        <MonthSelect label="To month" value={filters.to} onChange={set("to")} />
        <FilterSelect label="Insurer" value={filters.insurer} onChange={set("insurer")} options={lookups["insurer-companies"]}>
          {COMPANY_GROUP_KEYS.map((g) => <option key={g} value={g}>{g} (group)</option>)}
        </FilterSelect>
        <FilterSelect label="Assessor" value={filters.assessor_id} onChange={set("assessor_id")} options={lookups.assessors} />
        <FilterSelect label="Assessor Company" value={filters.assessor_company_id} onChange={set("assessor_company_id")} options={lookups["assessor-companies"]} />
        <FilterSelect label="Division" value={filters.division_id} onChange={set("division_id")} options={lookups.divisions} width={150} />
        <Form.Check
          type="switch" id="quoted-only" label="Jobs quoted only" className="mb-1"
          checked={!!filters.quoted_only} onChange={(e) => set("quoted_only")(e.target.checked)}
        />
        <div className="d-flex column-gap-3 mb-1">
          {BREAKDOWNS.map(([key, label]) => (
            <Form.Check
              key={key} type="checkbox" id={`bd-${key}`} label={label}
              checked={dims.includes(key)} onChange={() => toggleDim(key)}
            />
          ))}
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <div className="report-doc border rounded">
          <ReportMasthead
            report="Strike Rate"
            title={scopeLabel}
            subtitle={`Grayburn Property Services · ${monthLabelLong(filters.from)} to ${monthLabelLong(filters.to)}`}
          />
          <div className="report-body">
          <RateTable rows={report.months} reasons={report.decline_reasons} />
          {Object.entries(report.breakdowns).map(([dim, rows]) => (
            <div key={dim} className="mt-4">
              <h6 className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
                {BREAKDOWNS.find(([k]) => k === dim)?.[1] || dim}
              </h6>
              <RateTable rows={rows} reasons={report.decline_reasons} groupCol={dim[0].toUpperCase() + dim.slice(1)} />
            </div>
          ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default StrikeRateReportPage;
